/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { slackDirectoryConnectionsSetDefaultProject } from "../funcs/slackDirectoryConnectionsSetDefaultProject.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SlackDirectoryConnection } from "../models/components/slackdirectoryconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetSlackDirectoryDefaultProjectRequest,
  SetSlackDirectoryDefaultProjectSecurity,
} from "../models/operations/setslackdirectorydefaultproject.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetSlackDirectoryDefaultProjectMutationVariables = {
  request: SetSlackDirectoryDefaultProjectRequest;
  security?: SetSlackDirectoryDefaultProjectSecurity | undefined;
  options?: RequestOptions;
};

export type SetSlackDirectoryDefaultProjectMutationData =
  SlackDirectoryConnection;

export type SetSlackDirectoryDefaultProjectMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setDefaultProject slackDirectoryConnections
 *
 * @remarks
 * Choose the project whose managed assistant answers Speakeasy app mentions and direct messages in a connected workspace. Omit project_id to clear it. A project can be the default for at most one workspace.
 */
export function useSetSlackDirectoryDefaultProjectMutation(
  options?: MutationHookOptions<
    SetSlackDirectoryDefaultProjectMutationData,
    SetSlackDirectoryDefaultProjectMutationError,
    SetSlackDirectoryDefaultProjectMutationVariables
  >,
): UseMutationResult<
  SetSlackDirectoryDefaultProjectMutationData,
  SetSlackDirectoryDefaultProjectMutationError,
  SetSlackDirectoryDefaultProjectMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetSlackDirectoryDefaultProjectMutation(client, options),
    ...options,
  });
}

export function mutationKeySetSlackDirectoryDefaultProject(): MutationKey {
  return ["@gram/client", "slackDirectoryConnections", "setDefaultProject"];
}

export function buildSetSlackDirectoryDefaultProjectMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetSlackDirectoryDefaultProjectMutationVariables,
  ) => Promise<SetSlackDirectoryDefaultProjectMutationData>;
} {
  return {
    mutationKey: mutationKeySetSlackDirectoryDefaultProject(),
    mutationFn: function setSlackDirectoryDefaultProjectMutationFn({
      request,
      security,
      options,
    }): Promise<SetSlackDirectoryDefaultProjectMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(slackDirectoryConnectionsSetDefaultProject(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
